import React from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Flex, Text, Image as BaseImage, Skeleton, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import BaseLink from '@amblique/base/app/components/link';
import {resolveConsultationCalendlyCta} from '@amblique/king-living/app/constants/calendly-booking-links';
import {useCalendlyConsultationLinks} from '@amblique/king-living/app/contexts/calendly-consultation-links-context';
import {resolveContentfulLinkTargetProps} from '@amblique/king-living/app/contentful/helpers/resolve-contentful-link-url';
import {scrollElementBelowStickyHeader} from '@amblique/king-living/app/utils/utils';

/**
 * Maps Contentful link type to BaseLink variant (shared for normal and Calendly fallback links).
 *
 * @param {string | undefined} linkType
 * @returns {string}
 */
const getContentfulLinkVariant = (linkType) => {
  const normalizedType =
    typeof linkType === 'string' ? linkType.trim().toLowerCase().replace(/\s+/g, ' ') : '';
  const noSpace = normalizedType.replace(/\s/g, '');

  switch (normalizedType) {
    case 'primary':
      return 'filled-light';
    case 'secondary':
      return 'outlined-light';
    case 'filled dark':
      return 'filled-dark';
    case 'outlined dark':
      return 'outlined-dark';
    case 'card':
      return 'base';
    default:
      break;
  }

  if (noSpace === 'filleddark') {
    return 'filled-dark';
  }

  if (noSpace === 'outlineddark') {
    return 'outlined-dark';
  }

  if (noSpace === 'filledlight') {
    return 'filled-light';
  }

  if (noSpace === 'outlinedlight') {
    return 'outlined-light';
  }

  return 'base';
};

/**
 * @override
 * Link component from Contentful
 * @param {Object} props - Component props
 * @param {Contentful.Link} props.component - Link component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {React.ReactNode} [props.children] - Additional content to render inside link
 * @returns {React.ReactElement}
 */
export const Link = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {linkText, url, openInNewTab, linkType, image, helptext, iconPosition} = component;
  const text = linkText ?? component.text;
  const type = linkType;
  const styles = useMultiStyleConfig('contentful/link', {component, type, iconPosition});
  const consultationCalendly = useCalendlyConsultationLinks();
  const consultationOutcome = resolveConsultationCalendlyCta(url, consultationCalendly);
  const {formatMessage} = useIntl();

  if (consultationOutcome.type === 'hidden') {
    return null;
  }

  // Show skeleton while fetching
  if (!isComplete && isFetching) {
    return (
      <Skeleton
        height="48px"
        width="90px"
      />
    );
  }

  if (consultationOutcome.type === 'findShowroom') {
    const findTarget = resolveContentfulLinkTargetProps(consultationOutcome.href);
    const findVariant = getContentfulLinkVariant(type);
    const findLinkProps = {
      href: findTarget.href,
      to: findTarget.to,
      target: openInNewTab ? '_blank' : undefined,
      rel: openInNewTab ? 'noopener noreferrer' : undefined,
      title: formatMessage({
        id: 'calendly.find_showroom.assistive',
        defaultMessage: 'Find a King showroom',
      }),
      variant: findVariant,
      ...styles.root,
    };
    const findClassName = `link link--${findVariant}`;

    return (
      <BaseLink
        {...findLinkProps}
        className={findClassName}
      >
        <Text {...styles.text}>
          {formatMessage({
            id: 'calendly.find_showroom',
            defaultMessage: 'Find a showroom',
          })}
        </Text>
      </BaseLink>
    );
  }

  const displayUrl = consultationOutcome.url;
  const {
    displayUrl: hashDisplayUrl,
    href: linkHref,
    isHashOnly,
    to: linkTo,
  } = resolveContentfulLinkTargetProps(displayUrl);

  const variant = getContentfulLinkVariant(type);
  const hasImage = Boolean(image?.url);

  // Normalize iconPosition to handle case-insensitive and whitespace
  const normalizedIconPosition = iconPosition?.toString().toLowerCase().trim();
  const isValidIconPosition = normalizedIconPosition === 'left' || normalizedIconPosition === 'right';

  // Determine display logic: if iconPosition exists and image is present, display both image and text
  const shouldDisplayBoth = isValidIconPosition && hasImage;
  const shouldDisplayImageOnly = hasImage && !shouldDisplayBoth;

  const linkProps = {
    href: linkHref,
    to: linkTo,
    target: openInNewTab ? '_blank' : undefined,
    rel: openInNewTab ? 'noopener noreferrer' : undefined,
    title: helptext || text,
    variant,
    ...(isHashOnly && {
      onClick: (event) => {
        event.preventDefault();
        scrollElementBelowStickyHeader(
          hashDisplayUrl,
          () => {
            if (typeof window !== 'undefined') {
              window.history.pushState(null, '', hashDisplayUrl);
            }
          },
          {recheckAfterLayout: true}
        );
      },
    }),
    ...styles.root,
  };

  const linkClassName = `link link--${variant} ${shouldDisplayBoth ? 'link--icon-on-' + normalizedIconPosition : ''}`;

  // Render content based on display mode
  const renderContent = () => {
    // Custom children override everything
    if (props.children) {
      return props.children;
    }

    // Case 1: Display both image and text (when iconPosition is set)
    // Use Flex so icon and text stay in one row (avoid stacking on top of each other)
    if (shouldDisplayBoth) {
      return (
        <Flex
          as="span"
          alignItems="center"
          display="inline-flex"
          flexDirection="row"
          gap="8px"
        >
          {normalizedIconPosition === 'right' ? (
            <>
              <Text {...styles.text}>{text}</Text>
              <span {...styles.imageWrapper}>
                <BaseImage
                  {...styles.image}
                  alt={image.title || text || ''}
                  src={image.url}
                />
              </span>
            </>
          ) : (
            <>
              <span {...styles.imageWrapper}>
                <BaseImage
                  {...styles.image}
                  alt={image.title || text || ''}
                  src={image.url}
                />
              </span>
              <Text {...styles.text}>{text}</Text>
            </>
          )}
        </Flex>
      );
    }

    // Case 2: Display image only
    if (shouldDisplayImageOnly) {
      return (
        <BaseImage
          {...styles.image}
          alt={image.title || text || ''}
          src={image.url}
        />
      );
    }

    // Case 3: Display text only (always available since link always has text)
    return (
      <Text {...styles.text}>{text}</Text>
    );
  };

  return (
    <BaseLink
      {...linkProps}
      className={linkClassName}
    >
      {renderContent()}
    </BaseLink>
  );
};

Link.displayName = 'Link';
Link.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
